import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BellIcon,
  ChevronDownIcon,
  LogoutIcon,
  MenuIcon,
  UserCircleIcon,
} from "@heroicons/react/outline";
import { useAuth } from "../../context/AuthContext";
import { useSidebar } from "../../context/SidebarContext";
import { canAccessAction } from "../../routes/routePermissions";

const formatPageTitle = (pathname: string) => {
  const parts = pathname.split("/").filter(Boolean).slice(1);
  const lastPart = parts[parts.length - 1] || "dashboard";

  if (/^\d+$/.test(lastPart) && parts.length > 1) {
    return parts[parts.length - 2];
  }

  return lastPart
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const Header: React.FC = () => {
  const { user, logout, permissions, roles } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth, openSidebar } = useSidebar();
  const [isMobile, setIsMobile] = useState(false);
  const [showProfileOptions, setShowProfileOptions] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const profileOptionsRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileOptionsRef.current && !profileOptionsRef.current.contains(event.target as Node)) {
        setShowProfileOptions(false);
      }

      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const pageTitle = useMemo(() => formatPageTitle(location.pathname), [location.pathname]);
  const canOpenSettings = useMemo(() => canAccessAction("configure_settings", permissions, roles), [permissions, roles]);
  const userName = user?.name || "Admin User";
  const userInitials = userName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header
      className="fixed right-0 top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white/95 px-3 backdrop-blur transition-all duration-200 md:px-5"
      style={{ left: isMobile ? 0 : `${sidebarWidth}px` }}
    >
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={openSidebar}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 lg:hidden"
          aria-label="Open navigation"
        >
          <MenuIcon aria-hidden="true" className="h-4 w-4 stroke-[2.2]" />
        </button>

        <div className="min-w-0">
          <h1 className="cms-type-section-title truncate">{pageTitle}</h1>
          <p className="cms-type-helper hidden truncate sm:block">CMS Construction Admin</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative" ref={notificationsRef}>
          <button
            type="button"
            onClick={() => setShowNotifications((current) => !current)}
            className="relative flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-800"
            aria-label="Notifications"
            aria-haspopup="dialog"
            aria-expanded={showNotifications}
          >
            <BellIcon aria-hidden="true" className="h-4 w-4 stroke-[2.1]" />
          </button>

          {showNotifications && (
            <div
              role="dialog"
              aria-label="Notifications"
              className="absolute right-0 mt-2 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
            >
              <div className="border-b border-slate-100 px-4 py-3">
                <h2 className="cms-type-modal-title">Notifications</h2>
                <p className="cms-type-helper mt-1">Recent system updates and reminders.</p>
              </div>
              <div className="px-4 py-6 text-center">
                <BellIcon aria-hidden="true" className="mx-auto h-6 w-6 stroke-[1.8] text-slate-300" />
                <p className="cms-type-state-text mt-2 text-slate-700">No notifications</p>
                <p className="cms-type-helper mt-1">New alerts will appear here when available.</p>
              </div>
            </div>
          )}
        </div>

        <div className="relative" ref={profileOptionsRef}>
          <button
            type="button"
            onClick={() => setShowProfileOptions((current) => !current)}
            className="flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-white px-2 shadow-sm transition hover:bg-slate-50"
            aria-label="User menu"
          >
            <span className="cms-type-badge flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-700">
              {userInitials || <UserCircleIcon aria-hidden="true" className="h-4 w-4 stroke-[2]" />}
            </span>
            <span className="cms-type-button hidden max-w-[140px] truncate text-slate-700 sm:inline">{userName}</span>
            <ChevronDownIcon aria-hidden="true" className="h-3.5 w-3.5 stroke-[2.2] text-slate-400" />
          </button>

          {showProfileOptions && (
            <div className="absolute right-0 mt-2 w-48 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
              {canOpenSettings && (
                <button
                  type="button"
                  onClick={() => {
                    setShowProfileOptions(false);
                    navigate("/admin/settings");
                  }}
                  className="cms-type-button block w-full px-3 py-2 text-left text-slate-700 transition hover:bg-slate-50"
                >
                  Settings
                </button>
              )}
              <button
                type="button"
                onClick={handleLogout}
                className="cms-type-button flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2 text-left text-rose-600 transition hover:bg-rose-50"
              >
                <LogoutIcon aria-hidden="true" className="h-4 w-4 stroke-[2]" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
